import React, { useState, useEffect } from 'react';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import { Plus } from 'lucide-react';
import { Column } from '../components/Projects/Column';
import { projectsService } from '../services/projects';
import { LoadingSpinner } from '../components/UI/LoadingSpinner';
import { ProjectColumn } from '../types/projects';
import { usePermissions } from '../hooks/usePermissions';

export const Projects: React.FC = () => {
  const [columns, setColumns] = useState<ProjectColumn[]>([]);
  const [editingColumn, setEditingColumn] = useState<string | null>(null);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isSuperAdmin } = usePermissions();

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const initializeProjects = async () => {
      try {
        const initialColumns = await projectsService.initialize();
        setColumns(initialColumns);
        setError(null);

        unsubscribe = projectsService.subscribe(
          (updatedColumns) => {
            setColumns(updatedColumns);
            setError(null);
          },
          (err) => {
            console.error('Erreur de souscription:', err);
            setError(err.message);
          }
        );
      } catch (err: any) {
        console.error('Erreur d\'initialisation:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeProjects();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const handleDragEnd = async (result: DropResult) => {
    if (!isSuperAdmin) return;

    const { source, destination } = result;

    if (!destination ||
        (destination.droppableId === source.droppableId &&
         destination.index === source.index)) {
      return;
    }

    try {
      const newColumns = [...columns];
      const sourceColumn = newColumns.find((col) => col.id === source.droppableId);
      const destColumn = newColumns.find((col) => col.id === destination.droppableId);

      if (!sourceColumn || !destColumn) return;

      const [movedCard] = sourceColumn.cards.splice(source.index, 1);
      destColumn.cards.splice(destination.index, 0, movedCard);

      setColumns(newColumns);
      await projectsService.saveAll(newColumns);
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleAddColumn = async () => {
    if (!isSuperAdmin) return;

    try {
      const newColumn: ProjectColumn = {
        id: `col-${Date.now()}`,
        title: 'Nouvelle colonne',
        cards: [],
      };

      const newColumns = [...columns, newColumn];
      setColumns(newColumns);
      await projectsService.saveAll(newColumns);
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleColumnTitleSave = async (columnId: string) => {
    if (!isSuperAdmin || !newColumnTitle.trim()) return;

    try {
      const newColumns = columns.map(col =>
        col.id === columnId ? { ...col, title: newColumnTitle.trim() } : col
      );

      setColumns(newColumns);
      await projectsService.saveAll(newColumns);
      setEditingColumn(null);
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleDeleteColumn = async (columnId: string) => {
    if (!isSuperAdmin) return;

    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette colonne ?')) {
      return;
    }

    try {
      const newColumns = columns.filter(col => col.id !== columnId);
      setColumns(newColumns);
      await projectsService.saveAll(newColumns);
    } catch (error: any) {
      setError(error.message);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <header>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Projets</h1>
            <p className="text-gray-500 dark:text-gray-400">
              Gérez vos projets en cours
            </p>
          </div>
          {isSuperAdmin && (
            <button
              onClick={handleAddColumn}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Nouvelle Colonne
            </button>
          )}
        </div>
        {error && (
          <div className="mt-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg">
            {error}
          </div>
        )}
      </header>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-6 overflow-x-auto pb-4">
          {columns?.map(column => (
            <Column
              key={column.id}
              column={column}
              isEditing={editingColumn === column.id}
              newTitle={newColumnTitle}
              onTitleChange={setNewColumnTitle}
              onTitleSave={() => handleColumnTitleSave(column.id)}
              onEditStart={() => {
                if (isSuperAdmin) {
                  setEditingColumn(column.id);
                  setNewColumnTitle(column.title);
                }
              }}
              onDelete={() => handleDeleteColumn(column.id)}
              setColumns={setColumns}
              readOnly={!isSuperAdmin}
            />
          ))}
        </div>
      </DragDropContext>
    </div>
  );
};
